/**
 * Classificação determinística do ÂMBITO GEOGRÁFICO de um pedido
 * (cidade / região / país / várias cidades), sem IA, sem Maps e sem créditos.
 *
 * Regra dura: um país NUNCA pode entrar no pipeline como cidade. O nome
 * apresentado é sempre localizado ("Suíça"), nunca "<name> Country".
 *
 * Não há hardcode de comportamento por país: existe apenas um dicionário de
 * nomes (dados), igual para todos.
 */

export type GeoScopeKind = "city" | "region" | "country" | "multi_city" | "unknown";

export type GeoScopeResult = {
  kind: GeoScopeKind;
  /** Nome já localizado para apresentação. */
  name: string;
  /** ISO 3166-1 alpha-2 quando conhecido. */
  countryCode?: string;
  /** Partes detetadas quando `kind === "multi_city"`. */
  parts?: string[];
  /** Como chegámos à classificação (diagnóstico). */
  via: "geocode" | "dictionary" | "separator" | "fallback";
};

/** Subconjunto do resultado do Geocoder que nos interessa. */
export type GeocodeHint = {
  types?: string[] | null;
  addressComponents?: Array<{ types?: string[] | null; longName?: string | null; shortName?: string | null }> | null;
  formattedAddress?: string | null;
};

export const fold = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase().replace(/\s+/gu, " ").trim();

type CountryEntry = {
  code: string;
  /** Nome por idioma; `en` é obrigatório e serve de fallback. */
  names: { en: string; pt?: string; es?: string; fr?: string; it?: string; de?: string };
  /** Aliases adicionais (formas curtas, demónimos, grafias antigas). */
  aliases?: string[];
};

/** Dados, não regras: nenhum país tem tratamento especial no código. */
const COUNTRIES: CountryEntry[] = [
  { code: "PT", names: { en: "Portugal", pt: "Portugal" } },
  { code: "ES", names: { en: "Spain", pt: "Espanha", es: "España", fr: "Espagne", it: "Spagna", de: "Spanien" } },
  { code: "FR", names: { en: "France", pt: "França", es: "Francia", it: "Francia", de: "Frankreich" } },
  { code: "IT", names: { en: "Italy", pt: "Itália", es: "Italia", fr: "Italie", it: "Italia", de: "Italien" } },
  { code: "CH", names: { en: "Switzerland", pt: "Suíça", es: "Suiza", fr: "Suisse", it: "Svizzera", de: "Schweiz" }, aliases: ["helvetia"] },
  { code: "AT", names: { en: "Austria", pt: "Áustria", es: "Austria", fr: "Autriche", it: "Austria", de: "Österreich" } },
  { code: "DE", names: { en: "Germany", pt: "Alemanha", es: "Alemania", fr: "Allemagne", it: "Germania", de: "Deutschland" } },
  { code: "NL", names: { en: "Netherlands", pt: "Países Baixos", es: "Países Bajos", fr: "Pays-Bas", it: "Paesi Bassi", de: "Niederlande" }, aliases: ["holanda", "holland"] },
  { code: "BE", names: { en: "Belgium", pt: "Bélgica", es: "Bélgica", fr: "Belgique", it: "Belgio", de: "Belgien" } },
  { code: "LU", names: { en: "Luxembourg", pt: "Luxemburgo", de: "Luxemburg" } },
  { code: "GB", names: { en: "United Kingdom", pt: "Reino Unido", es: "Reino Unido", fr: "Royaume-Uni", it: "Regno Unito", de: "Vereinigtes Königreich" }, aliases: ["inglaterra", "england", "escocia", "scotland", "wales", "pais de gales", "gra-bretanha", "great britain", "uk"] },
  { code: "IE", names: { en: "Ireland", pt: "Irlanda", es: "Irlanda", fr: "Irlande", it: "Irlanda", de: "Irland" } },
  { code: "IS", names: { en: "Iceland", pt: "Islândia", es: "Islandia", fr: "Islande", it: "Islanda", de: "Island" } },
  { code: "NO", names: { en: "Norway", pt: "Noruega", es: "Noruega", fr: "Norvège", it: "Norvegia", de: "Norwegen" } },
  { code: "SE", names: { en: "Sweden", pt: "Suécia", es: "Suecia", fr: "Suède", it: "Svezia", de: "Schweden" } },
  { code: "FI", names: { en: "Finland", pt: "Finlândia", es: "Finlandia", fr: "Finlande", it: "Finlandia", de: "Finnland" } },
  { code: "DK", names: { en: "Denmark", pt: "Dinamarca", es: "Dinamarca", fr: "Danemark", it: "Danimarca", de: "Dänemark" } },
  { code: "PL", names: { en: "Poland", pt: "Polónia", es: "Polonia", fr: "Pologne", it: "Polonia", de: "Polen" } },
  { code: "CZ", names: { en: "Czechia", pt: "República Checa", es: "República Checa", fr: "Tchéquie", it: "Repubblica Ceca", de: "Tschechien" }, aliases: ["chequia", "czech republic"] },
  { code: "SK", names: { en: "Slovakia", pt: "Eslováquia", de: "Slowakei" } },
  { code: "HU", names: { en: "Hungary", pt: "Hungria", es: "Hungría", fr: "Hongrie", it: "Ungheria", de: "Ungarn" } },
  { code: "SI", names: { en: "Slovenia", pt: "Eslovénia", es: "Eslovenia", de: "Slowenien" } },
  { code: "HR", names: { en: "Croatia", pt: "Croácia", es: "Croacia", fr: "Croatie", it: "Croazia", de: "Kroatien" } },
  { code: "GR", names: { en: "Greece", pt: "Grécia", es: "Grecia", fr: "Grèce", it: "Grecia", de: "Griechenland" } },
  { code: "TR", names: { en: "Turkey", pt: "Turquia", es: "Turquía", fr: "Turquie", it: "Turchia", de: "Türkei" }, aliases: ["turkiye"] },
  { code: "RO", names: { en: "Romania", pt: "Roménia", es: "Rumanía", fr: "Roumanie", it: "Romania", de: "Rumänien" } },
  { code: "BG", names: { en: "Bulgaria", pt: "Bulgária", de: "Bulgarien" } },
  { code: "RS", names: { en: "Serbia", pt: "Sérvia", de: "Serbien" } },
  { code: "AL", names: { en: "Albania", pt: "Albânia", de: "Albanien" } },
  { code: "ME", names: { en: "Montenegro", pt: "Montenegro" } },
  { code: "EE", names: { en: "Estonia", pt: "Estónia", de: "Estland" } },
  { code: "LV", names: { en: "Latvia", pt: "Letónia", de: "Lettland" } },
  { code: "LT", names: { en: "Lithuania", pt: "Lituânia", de: "Litauen" } },
  { code: "MT", names: { en: "Malta", pt: "Malta" } },
  { code: "CY", names: { en: "Cyprus", pt: "Chipre", de: "Zypern" } },
  { code: "MA", names: { en: "Morocco", pt: "Marrocos", es: "Marruecos", fr: "Maroc", it: "Marocco", de: "Marokko" } },
  { code: "EG", names: { en: "Egypt", pt: "Egito", es: "Egipto", fr: "Égypte", it: "Egitto", de: "Ägypten" } },
  { code: "TN", names: { en: "Tunisia", pt: "Tunísia", de: "Tunesien" } },
  { code: "ZA", names: { en: "South Africa", pt: "África do Sul", es: "Sudáfrica", fr: "Afrique du Sud", de: "Südafrika" } },
  { code: "CV", names: { en: "Cape Verde", pt: "Cabo Verde" } },
  { code: "US", names: { en: "United States", pt: "Estados Unidos", es: "Estados Unidos", fr: "États-Unis", it: "Stati Uniti", de: "Vereinigte Staaten" }, aliases: ["eua", "usa", "america", "estados unidos da america"] },
  { code: "CA", names: { en: "Canada", pt: "Canadá", fr: "Canada", de: "Kanada" } },
  { code: "MX", names: { en: "Mexico", pt: "México", es: "México", de: "Mexiko" } },
  { code: "BR", names: { en: "Brazil", pt: "Brasil", es: "Brasil", fr: "Brésil", it: "Brasile", de: "Brasilien" } },
  { code: "AR", names: { en: "Argentina", pt: "Argentina", de: "Argentinien" } },
  { code: "CL", names: { en: "Chile", pt: "Chile" } },
  { code: "PE", names: { en: "Peru", pt: "Peru" } },
  { code: "CO", names: { en: "Colombia", pt: "Colômbia", de: "Kolumbien" } },
  { code: "UY", names: { en: "Uruguay", pt: "Uruguai" } },
  { code: "CR", names: { en: "Costa Rica", pt: "Costa Rica" } },
  { code: "CU", names: { en: "Cuba", pt: "Cuba" } },
  { code: "JP", names: { en: "Japan", pt: "Japão", es: "Japón", fr: "Japon", it: "Giappone", de: "Japan" } },
  { code: "CN", names: { en: "China", pt: "China" } },
  { code: "KR", names: { en: "South Korea", pt: "Coreia do Sul", es: "Corea del Sur", de: "Südkorea" } },
  { code: "TH", names: { en: "Thailand", pt: "Tailândia", es: "Tailandia", fr: "Thaïlande", de: "Thailand" } },
  { code: "VN", names: { en: "Vietnam", pt: "Vietname", es: "Vietnam", de: "Vietnam" } },
  { code: "ID", names: { en: "Indonesia", pt: "Indonésia", de: "Indonesien" } },
  { code: "IN", names: { en: "India", pt: "Índia", de: "Indien" } },
  { code: "SG", names: { en: "Singapore", pt: "Singapura", de: "Singapur" } },
  { code: "MY", names: { en: "Malaysia", pt: "Malásia", de: "Malaysia" } },
  { code: "PH", names: { en: "Philippines", pt: "Filipinas", de: "Philippinen" } },
  { code: "AE", names: { en: "United Arab Emirates", pt: "Emirados Árabes Unidos", de: "Vereinigte Arabische Emirate" }, aliases: ["dubai emirates", "uae"] },
  { code: "AU", names: { en: "Australia", pt: "Austrália", de: "Australien" } },
  { code: "NZ", names: { en: "New Zealand", pt: "Nova Zelândia", fr: "Nouvelle-Zélande", de: "Neuseeland" } },
  { code: "AD", names: { en: "Andorra", pt: "Andorra" } },
  { code: "BA", names: { en: "Bosnia and Herzegovina", pt: "Bósnia e Herzegovina" }, aliases: ["bosnia", "bosnia herzegovina", "bosnia y herzegovina"] },
  { code: "MK", names: { en: "North Macedonia", pt: "Macedónia do Norte" }, aliases: ["macedonia", "macedonia do norte", "makedonien"] },
  { code: "MC", names: { en: "Monaco", pt: "Mónaco" }, aliases: ["monaco"] },
  { code: "SM", names: { en: "San Marino", pt: "San Marino" }, aliases: ["sao marinho"] },
  { code: "VA", names: { en: "Vatican City", pt: "Vaticano" }, aliases: ["vatican", "cidade do vaticano"] },
  { code: "BY", names: { en: "Belarus", pt: "Bielorrússia" }, aliases: ["belarus", "bielorrussia"] },
  { code: "MD", names: { en: "Moldova", pt: "Moldávia" }, aliases: ["moldova"] },
  { code: "UA", names: { en: "Ukraine", pt: "Ucrânia" }, aliases: ["ukraine"] },
  { code: "RU", names: { en: "Russia", pt: "Rússia" }, aliases: ["russia", "russian federation", "rusia"] },
  { code: "LI", names: { en: "Liechtenstein", pt: "Liechtenstein" } },
  { code: "SA", names: { en: "Saudi Arabia", pt: "Arábia Saudita" }, aliases: ["arabia saudita", "saudi"] },
  { code: "MV", names: { en: "Maldives", pt: "Maldivas" } },
  { code: "LK", names: { en: "Sri Lanka", pt: "Sri Lanka" }, aliases: ["ceilao"] },
  { code: "JO", names: { en: "Jordan", pt: "Jordânia" } },
  { code: "QA", names: { en: "Qatar", pt: "Catar" }, aliases: ["qatar"] },
  { code: "OM", names: { en: "Oman", pt: "Omã" }, aliases: ["oma"] },
  { code: "NP", names: { en: "Nepal", pt: "Nepal" } },
  { code: "KH", names: { en: "Cambodia", pt: "Camboja" } },
  { code: "GE", names: { en: "Georgia", pt: "Geórgia" }, aliases: ["georgia"] },
  { code: "AM", names: { en: "Armenia", pt: "Arménia" }, aliases: ["armenia"] },
  { code: "AZ", names: { en: "Azerbaijan", pt: "Azerbaijão" }, aliases: ["azerbaijao"] },
  { code: "BH", names: { en: "Bahrain", pt: "Bahrein" } },
  { code: "BD", names: { en: "Bangladesh", pt: "Bangladesh" } },
  { code: "BT", names: { en: "Bhutan", pt: "Butão" }, aliases: ["butao"] },
  { code: "BN", names: { en: "Brunei", pt: "Brunei" } },
  { code: "KZ", names: { en: "Kazakhstan", pt: "Cazaquistão" }, aliases: ["kazakhstan"] },
  { code: "KG", names: { en: "Kyrgyzstan", pt: "Quirguistão" }, aliases: ["kyrgyzstan"] },
  { code: "LA", names: { en: "Laos", pt: "Laos" } },
  { code: "LB", names: { en: "Lebanon", pt: "Líbano" }, aliases: ["libano"] },
  { code: "MN", names: { en: "Mongolia", pt: "Mongólia" }, aliases: ["mongolia"] },
  { code: "PK", names: { en: "Pakistan", pt: "Paquistão" }, aliases: ["pakistan"] },
  { code: "TJ", names: { en: "Tajikistan", pt: "Tajiquistão" }, aliases: ["tajikistan"] },
  { code: "TL", names: { en: "Timor-Leste", pt: "Timor-Leste" }, aliases: ["timor leste", "east timor"] },
  { code: "TM", names: { en: "Turkmenistan", pt: "Turquemenistão" }, aliases: ["turkmenistan"] },
  { code: "UZ", names: { en: "Uzbekistan", pt: "Uzbequistão" }, aliases: ["uzbekistan"] },
  { code: "IL", names: { en: "Israel", pt: "Israel" } },
  { code: "AF", names: { en: "Afghanistan", pt: "Afeganistão" }, aliases: ["afghanistan"] },
  { code: "KP", names: { en: "North Korea", pt: "Coreia do Norte" } },
  { code: "YE", names: { en: "Yemen", pt: "Iémen" }, aliases: ["iemen", "yemen"] },
  { code: "IQ", names: { en: "Iraq", pt: "Iraque" }, aliases: ["iraq"] },
  { code: "IR", names: { en: "Iran", pt: "Irão" }, aliases: ["irao", "iran"] },
  { code: "MM", names: { en: "Myanmar", pt: "Myanmar" }, aliases: ["birmania", "burma"] },
  { code: "SY", names: { en: "Syria", pt: "Síria" }, aliases: ["siria"] },
  { code: "KW", names: { en: "Kuwait", pt: "Kuwait" } },
  { code: "PS", names: { en: "Palestine", pt: "Palestina" } },
  { code: "DO", names: { en: "Dominican Republic", pt: "República Dominicana" }, aliases: ["republica dominicana"] },
  { code: "JM", names: { en: "Jamaica", pt: "Jamaica" } },
  { code: "BS", names: { en: "Bahamas", pt: "Bahamas" } },
  { code: "PA", names: { en: "Panama", pt: "Panamá" }, aliases: ["panama"] },
  { code: "BB", names: { en: "Barbados", pt: "Barbados" } },
  { code: "AG", names: { en: "Antigua and Barbuda", pt: "Antígua e Barbuda" }, aliases: ["antigua", "antigua e barbuda"] },
  { code: "BZ", names: { en: "Belize", pt: "Belize" } },
  { code: "DM", names: { en: "Dominica", pt: "Dominica" } },
  { code: "SV", names: { en: "El Salvador", pt: "El Salvador" } },
  { code: "GD", names: { en: "Grenada", pt: "Granada (Caraíbas)" }, aliases: ["grenada"] },
  { code: "GT", names: { en: "Guatemala", pt: "Guatemala" } },
  { code: "HT", names: { en: "Haiti", pt: "Haiti" } },
  { code: "HN", names: { en: "Honduras", pt: "Honduras" } },
  { code: "NI", names: { en: "Nicaragua", pt: "Nicarágua" }, aliases: ["nicaragua"] },
  { code: "LC", names: { en: "Saint Lucia", pt: "Santa Lúcia" }, aliases: ["santa lucia", "saint lucia"] },
  { code: "KN", names: { en: "Saint Kitts and Nevis", pt: "São Cristóvão e Neves" }, aliases: ["sao cristovao e neves", "saint kitts"] },
  { code: "VC", names: { en: "Saint Vincent and the Grenadines", pt: "São Vicente e Granadinas" }, aliases: ["sao vicente e granadinas"] },
  { code: "TT", names: { en: "Trinidad and Tobago", pt: "Trindade e Tobago" }, aliases: ["trinidad e tobago", "trinidad"] },
  { code: "EC", names: { en: "Ecuador", pt: "Equador" }, aliases: ["ecuador"] },
  { code: "BO", names: { en: "Bolivia", pt: "Bolívia" }, aliases: ["bolivia"] },
  { code: "PY", names: { en: "Paraguay", pt: "Paraguai" }, aliases: ["paraguay"] },
  { code: "VE", names: { en: "Venezuela", pt: "Venezuela" } },
  { code: "GY", names: { en: "Guyana", pt: "Guiana" }, aliases: ["guyana"] },
  { code: "SR", names: { en: "Suriname", pt: "Suriname" } },
  { code: "MU", names: { en: "Mauritius", pt: "Maurícia" }, aliases: ["mauricia", "ilha mauricia", "mauricio"] },
  { code: "SC", names: { en: "Seychelles", pt: "Seicheles" }, aliases: ["seychelles"] },
  { code: "TZ", names: { en: "Tanzania", pt: "Tanzânia" }, aliases: ["tanzania", "zanzibar"] },
  { code: "KE", names: { en: "Kenya", pt: "Quénia" }, aliases: ["quenia", "kenya"] },
  { code: "NA", names: { en: "Namibia", pt: "Namíbia" }, aliases: ["namibia"] },
  { code: "BW", names: { en: "Botswana", pt: "Botsuana" }, aliases: ["botswana"] },
  { code: "DZ", names: { en: "Algeria", pt: "Argélia" }, aliases: ["argelia"] },
  { code: "AO", names: { en: "Angola", pt: "Angola" } },
  { code: "BJ", names: { en: "Benin", pt: "Benim" }, aliases: ["benin"] },
  { code: "CM", names: { en: "Cameroon", pt: "Camarões" }, aliases: ["camaroes"] },
  { code: "KM", names: { en: "Comoros", pt: "Comores" } },
  { code: "CI", names: { en: "Ivory Coast", pt: "Costa do Marfim" }, aliases: ["cote d'ivoire", "cote divoire"] },
  { code: "SZ", names: { en: "Eswatini", pt: "Essuatíni" }, aliases: ["essuatini", "suazilandia", "eswatini"] },
  { code: "ET", names: { en: "Ethiopia", pt: "Etiópia" }, aliases: ["etiopia"] },
  { code: "GA", names: { en: "Gabon", pt: "Gabão" }, aliases: ["gabao"] },
  { code: "GM", names: { en: "Gambia", pt: "Gâmbia" }, aliases: ["gambia"] },
  { code: "GH", names: { en: "Ghana", pt: "Gana" }, aliases: ["ghana"] },
  { code: "LS", names: { en: "Lesotho", pt: "Lesoto" }, aliases: ["lesotho"] },
  { code: "MG", names: { en: "Madagascar", pt: "Madagáscar" }, aliases: ["madagascar"] },
  { code: "MW", names: { en: "Malawi", pt: "Malawi" } },
  { code: "MZ", names: { en: "Mozambique", pt: "Moçambique" }, aliases: ["mocambique"] },
  { code: "RW", names: { en: "Rwanda", pt: "Ruanda" }, aliases: ["rwanda"] },
  { code: "ST", names: { en: "Sao Tome and Principe", pt: "São Tomé e Príncipe" }, aliases: ["sao tome e principe", "sao tome"] },
  { code: "SN", names: { en: "Senegal", pt: "Senegal" } },
  { code: "UG", names: { en: "Uganda", pt: "Uganda" } },
  { code: "ZM", names: { en: "Zambia", pt: "Zâmbia" }, aliases: ["zambia"] },
  { code: "ZW", names: { en: "Zimbabwe", pt: "Zimbabué" }, aliases: ["zimbabue", "zimbabwe"] },
  { code: "BF", names: { en: "Burkina Faso", pt: "Burkina Faso" } },
  { code: "BI", names: { en: "Burundi", pt: "Burundi" } },
  { code: "TD", names: { en: "Chad", pt: "Chade" }, aliases: ["chad"] },
  { code: "DJ", names: { en: "Djibouti", pt: "Djibuti" }, aliases: ["djibouti"] },
  { code: "ER", names: { en: "Eritrea", pt: "Eritreia" }, aliases: ["eritrea"] },
  { code: "GN", names: { en: "Guinea", pt: "Guiné" }, aliases: ["guine"] },
  { code: "GW", names: { en: "Guinea-Bissau", pt: "Guiné-Bissau" }, aliases: ["guine-bissau", "guine bissau"] },
  { code: "GQ", names: { en: "Equatorial Guinea", pt: "Guiné Equatorial" }, aliases: ["guine equatorial"] },
  { code: "LR", names: { en: "Liberia", pt: "Libéria" }, aliases: ["liberia"] },
  { code: "LY", names: { en: "Libya", pt: "Líbia" }, aliases: ["libia"] },
  { code: "ML", names: { en: "Mali", pt: "Mali" } },
  { code: "MR", names: { en: "Mauritania", pt: "Mauritânia" }, aliases: ["mauritania"] },
  { code: "NE", names: { en: "Niger", pt: "Níger" }, aliases: ["niger"] },
  { code: "NG", names: { en: "Nigeria", pt: "Nigéria" }, aliases: ["nigeria"] },
  { code: "CF", names: { en: "Central African Republic", pt: "República Centro-Africana" }, aliases: ["republica centro-africana"] },
  { code: "CD", names: { en: "Democratic Republic of the Congo", pt: "República Democrática do Congo" }, aliases: ["republica democratica do congo", "rd congo"] },
  { code: "CG", names: { en: "Republic of the Congo", pt: "República do Congo" }, aliases: ["republica do congo", "congo"] },
  { code: "SL", names: { en: "Sierra Leone", pt: "Serra Leoa" }, aliases: ["sierra leone"] },
  { code: "SO", names: { en: "Somalia", pt: "Somália" }, aliases: ["somalia"] },
  { code: "SD", names: { en: "Sudan", pt: "Sudão" }, aliases: ["sudao"] },
  { code: "SS", names: { en: "South Sudan", pt: "Sudão do Sul" }, aliases: ["sudao do sul"] },
  { code: "TG", names: { en: "Togo", pt: "Togo" } },
  { code: "FJ", names: { en: "Fiji", pt: "Fiji" } },
  { code: "PW", names: { en: "Palau", pt: "Palau" } },
  { code: "WS", names: { en: "Samoa", pt: "Samoa" } },
  { code: "TO", names: { en: "Tonga", pt: "Tonga" } },
  { code: "VU", names: { en: "Vanuatu", pt: "Vanuatu" } },
  { code: "FM", names: { en: "Micronesia", pt: "Micronésia" }, aliases: ["micronesia"] },
  { code: "KI", names: { en: "Kiribati", pt: "Kiribati" } },
  { code: "MH", names: { en: "Marshall Islands", pt: "Ilhas Marshall" }, aliases: ["ilhas marshall"] },
  { code: "NR", names: { en: "Nauru", pt: "Nauru" } },
  { code: "PG", names: { en: "Papua New Guinea", pt: "Papua-Nova Guiné" }, aliases: ["papua nova guine", "papua new guinea"] },
  { code: "SB", names: { en: "Solomon Islands", pt: "Ilhas Salomão" }, aliases: ["ilhas salomao"] },
  { code: "TV", names: { en: "Tuvalu", pt: "Tuvalu" } },
];

const COUNTRY_BY_ALIAS = new Map<string, CountryEntry>();
for (const entry of COUNTRIES) {
  const values = [...Object.values(entry.names), ...(entry.aliases ?? []), entry.code];
  for (const value of values) {
    const key = fold(value);
    if (key && !COUNTRY_BY_ALIAS.has(key)) COUNTRY_BY_ALIAS.set(key, entry);
  }
}

export function countryDisplayName(code: string, language = "pt"): string | undefined {
  const entry = COUNTRIES.find((c) => c.code === code.toUpperCase());
  if (!entry) return undefined;
  const names = entry.names as Record<string, string | undefined>;
  return names[language] ?? names.en;
}

/** Devolve o país quando o texto é (apenas) o nome de um país. */
export function matchCountry(value: string, language = "pt"): { code: string; name: string } | undefined {
  const entry = COUNTRY_BY_ALIAS.get(fold(value));
  if (!entry) return undefined;
  return { code: entry.code, name: countryDisplayName(entry.code, language) ?? entry.names.en };
}

/**
 * Procura um país mencionado DENTRO de um texto livre ("4 dias na Suíça de
 * comboio"). Devolve o alias mais longo para evitar falsos positivos curtos.
 */
export function findCountryInText(
  text: string,
  language = "pt",
): { code: string; name: string } | undefined {
  const folded = fold(text ?? "");
  if (!folded) return undefined;
  let best: { code: string; length: number } | undefined;
  for (const [alias, entry] of COUNTRY_BY_ALIAS) {
    if (alias.length < 3) continue;
    const re = new RegExp(`(^|[^a-z0-9])${alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`, "u");
    if (re.test(folded) && (!best || alias.length > best.length))
      best = { code: entry.code, length: alias.length };
  }
  if (!best) return undefined;
  return { code: best.code, name: countryDisplayName(best.code, language) ?? best.code };
}

const MULTI_SEPARATOR = /\s*(?:,|;|\/|\+|→|->|\be\b|\band\b|\by\b|\bet\b|\bund\b|\bed\b)\s*/iu;

const REGION_HINTS = [
  // sufixos/prefixos que denunciam uma região e não uma cidade
  /\b(?:regi[aã]o|region|regione|région|comarca|provincia|prov[ií]ncia|province|estado|state|condado|county|distrito|district|land(?:kreis)?|prefeitura|prefecture)\b/iu,
  /\b(?:costa|coast|k[uü]ste|riviera|litoral|algarve|highlands|lake\s+district|alpes|alps|alpi|alpen|dolomitas|dolomiti|dolomites|pirin[eé]us|pyren[eé]es|fiordes|fjords|sert[aã]o|pampas|patag[oó]nia|patagonia|toscana|tuscany|proven[çc]a|provence|andaluzia|andaluc[ií]a|baviera|bayern|bavaria|sic[ií]lia|sicilia|sicily|sardenha|sardegna|creta|crete|madeira|a[çc]ores|azores|canárias|canarias|canary\s+islands)\b/iu,
  /\b(?:norte|sul|centro|leste|oeste|north(?:ern)?|south(?:ern)?|east(?:ern)?|west(?:ern)?)\s+d[aeo]s?\s+\p{Lu}/u,
];

function looksLikeRegion(value: string): boolean {
  return REGION_HINTS.some((re) => re.test(value));
}

/**
 * Palavras que denunciam uma FRASE e não um topónimo. Genérico e
 * multilingue: nunca por cidade, país ou rota concreta.
 */
const SENTENCE_MARKERS =
  /\b(?:vou|vamos|quero|queremos|tenho|temos|estou|estamos|fazer|ver|visitar|conhecer|comer|ficar|passar|posso|podemos|gostava|gostaria|preciso|dias?|horas?|noites?|i|we|want|have|going|would|like|see|visit|eat|stay|days?|hours?|nights?|quiero|tengo|quiere|dias|horas|je|nous|veux|voulons|ai|avons|jours?|heures?|voglio|vorrei|ho|abbiamo|giorni|ore|ich|wir|will|m[oö]chte|habe|haben|tage|stunden)\b/iu;

/**
 * Um topónimo plausível: curto, sem números e sem verbos/pronomes. Impede que
 * "Vou a Lisboa, tenho 4 horas" seja lido como várias cidades.
 */
export function looksLikePlaceName(value: string): boolean {
  const raw = (value ?? "").replace(/\s+/gu, " ").trim();
  if (raw.length < 2 || raw.length > 60) return false;
  if (/\d/u.test(raw)) return false;
  if (raw.split(" ").length > 4) return false;
  if (SENTENCE_MARKERS.test(fold(raw))) return false;
  return true;
}

/** Classificação a partir do Geocoder (autoridade máxima). */
export function classifyFromGeocode(hint: GeocodeHint, language = "pt"): GeoScopeResult | undefined {
  const types = (hint.types ?? []).map((t) => t.toLowerCase());
  const components = hint.addressComponents ?? [];
  const countryComponent = components.find((c) => (c.types ?? []).includes("country"));
  const countryCode = countryComponent?.shortName?.toUpperCase();

  if (types.includes("country")) {
    const localized = countryCode ? countryDisplayName(countryCode, language) : undefined;
    return {
      kind: "country",
      name: localized ?? countryComponent?.longName ?? hint.formattedAddress ?? "",
      countryCode,
      via: "geocode",
    };
  }

  const isRegion =
    types.includes("administrative_area_level_1") ||
    types.includes("administrative_area_level_2") ||
    types.includes("archipelago");
  const isCity =
    types.includes("locality") ||
    types.includes("postal_town") ||
    types.includes("sublocality") ||
    types.includes("administrative_area_level_3");

  if (isCity) {
    return { kind: "city", name: hint.formattedAddress ?? "", countryCode, via: "geocode" };
  }
  if (isRegion) {
    return { kind: "region", name: hint.formattedAddress ?? "", countryCode, via: "geocode" };
  }
  return undefined;
}

/**
 * Classificação local. `geocode` (quando existir) tem sempre prioridade.
 */
export function classifyGeoScope(
  input: string,
  options: { language?: string; geocode?: GeocodeHint | null } = {},
): GeoScopeResult {
  const language = options.language ?? "pt";
  const raw = (input ?? "").replace(/\s+/gu, " ").trim();
  if (!raw) return { kind: "unknown", name: "", via: "fallback" };

  if (options.geocode) {
    const fromGeocode = classifyFromGeocode(options.geocode, language);
    if (fromGeocode && fromGeocode.name) return fromGeocode;
  }

  const country = matchCountry(raw, language);
  if (country) {
    return { kind: "country", name: country.name, countryCode: country.code, via: "dictionary" };
  }

  const parts = raw
    .split(MULTI_SEPARATOR)
    .map((p) => p.trim())
    .filter((p) => p.length >= 2);
  if (parts.length >= 2) {
    const countries = parts.map((p) => matchCountry(p, language)).filter(Boolean);
    if (countries.length === parts.length && countries[0]) {
      // "Suíça e Áustria" continua a ser um pedido de âmbito nacional.
      return {
        kind: "country",
        name: countries.map((c) => c!.name).join(" · "),
        countryCode: countries[0].code,
        parts: countries.map((c) => c!.name),
        via: "separator",
      };
    }
    // Só é multi-cidade quando TODAS as partes são topónimos plausíveis. Uma
    // frase ("Vou a Lisboa, tenho 4 horas") nunca é uma lista de cidades.
    if (parts.every(looksLikePlaceName)) {
      return { kind: "multi_city", name: parts.join(" · "), parts, via: "separator" };
    }
  }

  if (looksLikeRegion(raw)) {
    return { kind: "region", name: raw, via: "dictionary" };
  }

  // Frase completa sem topónimo isolável: âmbito desconhecido, nunca "cidade".
  if (!looksLikePlaceName(raw)) {
    return { kind: "unknown", name: raw, via: "fallback" };
  }

  return { kind: "city", name: raw, via: "fallback" };
}

/** Pedidos de âmbito alargado precisam do planeador hierárquico (Pro). */
export function needsRegionalPlanner(scope: GeoScopeResult): boolean {
  return scope.kind === "country" || scope.kind === "region" || scope.kind === "multi_city";
}

/** Nunca deixar um país entrar no pipeline como paragem/cidade. */
export function assertNotCountryAsCity(value: string): void {
  if (matchCountry(value)) {
    throw new Error(`geo_scope_country_used_as_city:${value}`);
  }
}
